/**
 * 🔔 Notifications Store (Zustand)
 *
 * مصدر واحد موحّد لإشعارات الجرس وصفحة "كل الإشعارات".
 *
 * يدمج مصدرين حيّين:
 *   1. notifications collection (مستندات firestore تكتبها Cloud Functions)
 *   2. booking_requests مباشرة (client-side، نفس مصدر قائمة الحجوزات الحيّ)
 *
 * لماذا store عالمي وليس حالة داخل المكوّن؟
 *   - الجرس (NotificationBell) مركّب مرتين في MainLayout (سطح مكتب + جوال).
 *     store عالمي + اشتراك idempotent (reference-counted) = stream واحد فقط
 *     مهما تعدّدت المكوّنات المستهلكة (تفادي مضاعفة قراءات Firestore).
 *   - صفحة /notifications تقرأ نفس المصدر → لا تناقض بين عدد الجرس والصفحة.
 *
 * حالة القراءة/الحذف:
 *   - firestore  → مستند حقيقي (markAsRead/delete على القاعدة)
 *   - booking    → لا مستند؛ الحالة في localStorage (seen-set) داخل NotificationService
 */

import { create } from 'zustand';
import { toast } from 'sonner';

import {
  notificationService,
  isBookingNotification,
  type AppNotification,
} from '@application/services/NotificationService';
import { playNotificationSound } from '@infrastructure/audio/notificationSound';

interface NotificationsState {
  notifications: AppNotification[];
  loading: boolean;
  unreadCount: number;

  /** يبدأ الاشتراكات (idempotent — reference-counted). يرجع دالة إيقاف. */
  init: () => () => void;

  markAsRead: (n: AppNotification) => Promise<void>;
  markAllAsRead: () => Promise<number>;
  remove: (n: AppNotification) => Promise<void>;
}

// آخر قائمة من كل مصدر (خارج الـ state — بيانات وسيطة للدمج فقط)
let firestoreList: AppNotification[] = [];
let bookingList: AppNotification[] = [];

// إدارة الاشتراك المشترك (reference counting)
let refCount = 0;
let unsubFirestore: (() => void) | null = null;
let unsubBookings: (() => void) | null = null;

// 🔔 كشف الطلب الجديد (صوت + toast).
//   knownBookingIds = null → لم تُؤسَّس القاعدة بعد (أول تحميل لا يُصدر صوتاً).
//   المفاتيح = relatedId (bookingId) لتوحيد المصدرين (راجع detectNewBookings).
let knownBookingIds: Set<string> | null = null;

/**
 * 🔔 إشعار بطلب جديد: صوت + toast قابل للنقر.
 *   يُستدعى مرة واحدة لكل طلب جديد فعلاً (الكشف داخل emit، والـ store singleton).
 */
function notifyNewBooking(n: AppNotification): void {
  playNotificationSound();
  toast(n.title, {
    description: n.body,
    action: {
      label: 'عرض الطلب',
      onClick: () => {
        window.location.href = n.route ?? '/bookings';
      },
    },
  });
}

/**
 * 🔎 يكشف الطلبات الجديدة (booking_created) بمقارنة مفاتيح الإشعارات:
 *   - أول emit يؤسّس القاعدة فقط (لا صوت — الطلبات القائمة ليست "جديدة").
 *   - لاحقاً: أي مفتاح لم يكن في القاعدة = طلب جديد → صوت + toast.
 *
 * ⚠️ المفتاح = relatedId (bookingId) وليس n.id، لتوحيد المصدرين:
 *    الإشعار المُشتقّ (id=`booking:{bid}`) والمستند (id=doc-id) يحملان نفس
 *    relatedId=bookingId. التوحيد يمنع صوتاً كاذباً عند وصول مستند firestore
 *    لطلب موجود أصلاً في القاعدة (بمعرّف مختلف) — بلا اعتماد على الوقت (الذي
 *    كان يخاطر بكتم طلب حقيقي لو ساعة الجهاز متقدّمة عن الخادم).
 *
 * يعمل على القائمة المدموجة، والكشف داخل emit المشترك → مرة واحدة لكل طلب
 * مهما تعدّدت المكوّنات المستهلكة (جرسان + الصفحة).
 */
function detectNewBookings(merged: AppNotification[]): void {
  const bookingNotifs = merged.filter((n) => n.type === 'booking_created');
  const keyOf = (n: AppNotification): string => n.relatedId ?? n.id;
  const nextKeys = new Set(bookingNotifs.map(keyOf));

  // أول emit: أسّس القاعدة فقط
  if (knownBookingIds === null) {
    knownBookingIds = nextKeys;
    return;
  }

  const baseline = knownBookingIds;
  const fresh = bookingNotifs.filter((n) => !baseline.has(keyOf(n)));

  // قدّم القاعدة (يُسقط أيضاً المفاتيح التي خرجت من الحالة النشطة)
  knownBookingIds = nextKeys;

  for (const n of fresh) notifyNewBooking(n);
}

/**
 * 🔗 يدمج المصدرين:
 *   - يُسقط الإشعار المُشتقّ من الحجز لو وُجد مستند firestore بنفس bookingId
 *     (تفادي الازدواج لو نُشرت Cloud Function لاحقاً)، مع ترحيل حالة "مقروء"
 *     المحلية إلى النسخة الباقية.
 *   - يعيد اشتقاق حالة "مقروء/مُخفى" للإشعارات المُشتقّة من seen-set مباشرة
 *     (بدل الاعتماد على النسخة القديمة المخزّنة) — فلا يُحيا إشعار محذوف/مقروء
 *     عند وصول تحديث من المصدر الآخر.
 */
function computeMerged(): AppNotification[] {
  const seen = notificationService.getSeenBookingIds();

  const firestoreBookingIds = new Set(
    firestoreList
      .filter((n) => n.type === 'booking_created' && n.relatedId)
      .map((n) => n.relatedId)
  );

  const derived = bookingList
    // أعد اشتقاق isRead من seen-set (المصدر الوحيد للحقيقة)
    .map((n) => ({ ...n, isRead: seen.has(n.relatedId ?? n.id) }))
    // أسقط ما عُلّم مقروءاً/مُخفى محلياً ووُجد مستند firestore يغطّيه
    .filter((n) => !firestoreBookingIds.has(n.relatedId));

  return [...firestoreList, ...derived]
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .slice(0, 50);
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  loading: true,
  unreadCount: 0,

  init: () => {
    refCount += 1;

    if (refCount === 1) {
      const emit = () => {
        const merged = computeMerged();
        set({
          notifications: merged,
          loading: false,
          unreadCount: merged.filter((n) => !n.isRead).length,
        });

        // 🔔 كشف الطلبات الجديدة (booking_created فقط) → صوت + toast
        detectNewBookings(merged);
      };

      unsubFirestore = notificationService.subscribe(
        (n) => {
          firestoreList = n;
          emit();
        },
        () => set({ loading: false })
      );

      unsubBookings = notificationService.subscribeBookingDerived(
        (n) => {
          bookingList = n;
          emit();
        },
        () => set({ loading: false })
      );
    }

    // دالة الإيقاف — تُفكّك الاشتراك فقط عند آخر مستهلك
    return () => {
      refCount -= 1;
      if (refCount <= 0) {
        refCount = 0;
        unsubFirestore?.();
        unsubBookings?.();
        unsubFirestore = null;
        unsubBookings = null;
        firestoreList = [];
        bookingList = [];
        // 🔔 صفّر قاعدة الكشف حتى لا يُصدر صوتاً لطلبات المستخدم الجديد القديمة
        //    عند logout→login بلا reload (يُعاد تأسيس القاعدة على أول emit).
        knownBookingIds = null;
        // 🔒 صفّر الـ state حتى لا تتسرّب إشعارات المستخدم السابق عند
        //    تسجيل الدخول بحساب آخر في نفس الجلسة (logout→login بلا reload).
        //    إعادة loading=true تُظهر الـ skeleton بدل بيانات قديمة.
        set({ notifications: [], unreadCount: 0, loading: true });
      }
    };
  },

  markAsRead: async (n) => {
    if (n.isRead) return;
    if (isBookingNotification(n)) {
      notificationService.markBookingSeen(n.relatedId ?? n.id);
    } else {
      await notificationService.markAsRead(n.id).catch(() => {});
    }
    // تحديث تفاؤلي فوري (سيؤكّده الـ snapshot التالي)
    const merged = computeMerged().map((x) =>
      x.id === n.id ? { ...x, isRead: true } : x
    );
    set({ notifications: merged, unreadCount: merged.filter((x) => !x.isRead).length });
  },

  markAllAsRead: async () => {
    const { notifications } = get();
    const unreadBookingIds = notifications
      .filter((n) => isBookingNotification(n) && !n.isRead)
      .map((n) => n.relatedId ?? n.id);
    if (unreadBookingIds.length > 0) {
      notificationService.markBookingsSeen(unreadBookingIds);
    }
    const count = await notificationService.markAllAsRead();

    const merged = computeMerged().map((x) => ({ ...x, isRead: true }));
    set({ notifications: merged, unreadCount: 0 });
    return count + unreadBookingIds.length;
  },

  remove: async (n) => {
    if (isBookingNotification(n)) {
      // لا مستند نحذفه — نُخفيه محلياً (نعلّمه seen)
      notificationService.markBookingSeen(n.relatedId ?? n.id);
    } else {
      await notificationService.delete(n.id);
    }
    const merged = computeMerged().filter((x) => x.id !== n.id);
    set({ notifications: merged, unreadCount: merged.filter((x) => !x.isRead).length });
  },
}));
